import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import {
  AllocationManagerResponse,
  AllocationManagerVersionsResponse,
  AllocationMethodUpdateResponse,
  CRMAssetResponse,
  DevAreaStatesResponse,
  DevelopmentAreaResponse,
  DevelopmentAreasCountResponse,
  DevelopmentAreaTagsResponse,
  DevelopmentAreaVersionsResponse,
  SlotNRIResponse,
  TitleAssetResponse,
  WellFeaturesResponse,
  WellsResponse,
} from 'types/api-responses';

import { createMutationAtom } from 'atoms/create-resource-atom';
import { useMemo } from 'react';
import { DEVELOPMENT_AREAS_API } from '../constants';
import { Subtracts, Tracts } from './allocation-manager-types';

const useDevelopmentAreaAtom = (href: string) => {
  return useMemo(
    () => createResourceAtom<DevelopmentAreaResponse>(href),
    [href]
  );
};

export const useDevelopmentAreaVersionAtom = (href?: string) => {
  return useMemo(
    () => createResourceAtom<DevelopmentAreaVersionsResponse>(href),
    [href]
  );
};

/**
 * A development area other than the one the page is on, by name.
 *
 * Slots Details lists a row per (well, agreement), so a row can belong to a
 * different unit. Selecting one moves the map to that unit, which means fetching
 * its geometry — the page's own development area only describes this unit.
 *
 * @param {string | null} name - Unit name, or null to skip the request.
 * @return {object} Resource atom for the unit, or a null atom when name is absent.
 */
const useUnitByNameAtom = (name: string | null) =>
  useMemo(() => {
    if (!name) return atom(null);
    return createResourceAtom<DevelopmentAreaResponse>(
      `/development_areas/${encodeURIComponent(name)}/versions/current`
    );
  }, [name]);

const useWellAtom = (uwi: string | null | undefined) =>
  useMemo(() => {
    if (!uwi) return atom(null);
    return createResourceAtom<WellsResponse>(`/wells/${uwi}`);
  }, [uwi]);

export const statesMutationAtom = createMutationAtom<
  DevAreaStatesResponse,
  'states'
>();

const useAllocationManagerAtom = (
  allocationManagerId: number | null,
  selectedVersion: string | null
) => {
  return useMemo(() => {
    if (!allocationManagerId || !selectedVersion) {
      return atom<AllocationManagerResponse | null>(null);
    }
    return createResourceAtom<AllocationManagerResponse>(
      `/allocation_managers/${allocationManagerId}?version=${selectedVersion}`
    );
  }, [allocationManagerId, selectedVersion]);
};

const useAllocationManagerVersionsAtom = (
  allocationManagerId: number | null,
  refreshKey: number
) => {
  return useMemo(() => {
    if (!allocationManagerId) {
      return atom(null);
    }
    return createResourceAtom<AllocationManagerVersionsResponse>(
      `/allocation_managers/${allocationManagerId}/versions`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationManagerId, refreshKey]);
};

const useWellFeaturesAtom = (wellId: string) => {
  return useMemo(() => {
    if (!wellId) {
      return atom(null);
    }
    return createResourceAtom<WellFeaturesResponse>(
      `/wells/${wellId}/features`
    );
  }, [wellId]);
};

const useTitleAssetsAtom = (
  allocationManagerId: number | null,
  developmentAreaId: number,
  overlapThreshold: number = 3,
  participationFactorThreshold: number | null
) => {
  return useMemo(() => {
    const thresholdValue = overlapThreshold / 100;

    let url;
    if (allocationManagerId) {
      url = `/allocation_managers/${allocationManagerId}/title_assets`;
    } else {
      url = `/development_areas/${developmentAreaId}/title_assets`;
    }

    const params = new URLSearchParams({
      min_intersection_percent: thresholdValue.toString(),
    });

    if (participationFactorThreshold) {
      params.append(
        'participation_factor_threshold',
        participationFactorThreshold.toString()
      );
    }

    url += `?${params.toString()}`;

    return createResourceAtom<TitleAssetResponse[]>(url);
  }, [
    allocationManagerId,
    developmentAreaId,
    overlapThreshold,
    participationFactorThreshold,
  ]);
};

export function convertRoyaltyRate(value: string): string {
  const trimmed = value.trim();

  if (trimmed === 'Custom') {
    return trimmed;
  }

  if (trimmed.endsWith('%')) {
    const numPart = trimmed.slice(0, -1);
    const parsed = parseFloat(numPart);
    if (isNaN(parsed)) {
      return trimmed;
    }
    return (parsed / 100).toString();
  }

  return trimmed;
}

const useCRMAssetsAtom = (
  allocationManagerId: number | null,
  developmentAreaId: number,
  participationFactorThreshold: number | null,
  defaultRoyaltyRate: string,
  overlapThreshold: number = 3
) => {
  return useMemo(() => {
    const thresholdValue = overlapThreshold / 100;

    let url;
    if (allocationManagerId) {
      url = `/allocation_managers/${allocationManagerId}/crm_assets`;
    } else {
      url = `/development_areas/${developmentAreaId}/crm_assets`;
    }

    const params = new URLSearchParams({
      min_intersection_percent: thresholdValue.toString(),
    });

    if (participationFactorThreshold) {
      params.append(
        'participation_factor_threshold',
        participationFactorThreshold.toString()
      );
    }

    if (!!defaultRoyaltyRate && defaultRoyaltyRate !== 'None') {
      const convertedRate = convertRoyaltyRate(defaultRoyaltyRate);
      params.append('default_royalty_rate', convertedRate);
    }

    url += `?${params.toString()}`;

    return createResourceAtom<CRMAssetResponse[]>(url);
  }, [
    allocationManagerId,
    developmentAreaId,
    overlapThreshold,
    defaultRoyaltyRate,
    participationFactorThreshold,
  ]);
};

const useUpdateAllocationManagerAtom = () =>
  createMutationAtom<any, 'allocationManager'>();

// Mutation atom for allocation method update
export const allocationMethodUpdateAtom = createMutationAtom<
  AllocationMethodUpdateResponse | null,
  'allocationMethodUpdate'
>();

const useSubtractDetailsAtom = (
  subtractId: string | null,
  version?: string | null
) => {
  return useMemo(() => {
    if (!subtractId) {
      return atom<Subtracts | null>(null);
    }

    const url = version
      ? `/crm/subtracts/${subtractId}?version=${version}`
      : `/crm/subtracts/${subtractId}`;

    return createResourceAtom<Subtracts>(url);
  }, [subtractId, version]);
};

const useTractDetailsAtom = (
  tractId: string | null,
  version?: string | null
) => {
  return useMemo(() => {
    if (!tractId) {
      return atom<Tracts | null>(null);
    }

    const url = version
      ? `/crm/tracts/${tractId}?version=${version}`
      : `/crm/tracts/${tractId}`;

    return createResourceAtom<Tracts>(url);
  }, [tractId, version]);
};

// Add atoms for version APIs
interface VersionsResponse {
  versions: string[];
  error?: never;
}

// Response type for error cases
interface ErrorResponse {
  error: string;
  versions?: never;
}

const useSubtractVersionsAtom = (subtractId: string | null) => {
  return useMemo(() => {
    if (!subtractId) {
      return atom<VersionsResponse | ErrorResponse | null>(null);
    }

    return createResourceAtom<VersionsResponse | ErrorResponse>(
      `/crm/subtracts/${subtractId}/versions`
    );
  }, [subtractId]);
};

const useTractVersionsAtom = (tractId: string | null) => {
  return useMemo(() => {
    if (!tractId) {
      return atom<VersionsResponse | ErrorResponse | null>(null);
    }

    return createResourceAtom<VersionsResponse | ErrorResponse>(
      `/crm/tracts/${tractId}/versions`
    );
  }, [tractId]);
};

export const developmentAreaIdAtom = atom<number | undefined>(undefined);

const useDevelopmentAreaNriAtom = (developmentAreaId: number | undefined) => {
  return useMemo(() => {
    return createResourceAtom<SlotNRIResponse>(
      developmentAreaId
        ? `/development_areas/${developmentAreaId}/slot_allocations`
        : undefined
    );
  }, [developmentAreaId]);
};

const useDevelopmentAreasCountAtom = () => {
  return useMemo(
    () =>
      createResourceAtom<DevelopmentAreasCountResponse>(
        DEVELOPMENT_AREAS_API.COUNT
      ),
    []
  );
};

/**
 * The tags in use on development areas, for the catalog's Tags filter.
 * @return {object} Resource atom for the tag options.
 */
const useDevelopmentAreaTagsAtom = () => {
  return useMemo(
    () =>
      createResourceAtom<DevelopmentAreaTagsResponse>(
        DEVELOPMENT_AREAS_API.TAGS
      ),
    []
  );
};

export {
  useAllocationManagerAtom,
  useUnitByNameAtom,
  useAllocationManagerVersionsAtom,
  useCRMAssetsAtom,
  useDevelopmentAreaAtom,
  useDevelopmentAreasCountAtom,
  useDevelopmentAreaTagsAtom,
  useSubtractDetailsAtom,
  useSubtractVersionsAtom,
  useTitleAssetsAtom,
  useTractDetailsAtom,
  useTractVersionsAtom,
  useUpdateAllocationManagerAtom,
  useWellAtom,
  useWellFeaturesAtom,
  useDevelopmentAreaNriAtom,
};
